import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { exportToExcel, exportToPDF, ExportColumn } from '@/lib/exportUtils';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { AuditLogRecord, ComplianceAlert, ComplianceMetrics } from '@/types/compliance';
import { actionCategoryLabels, sensitivityLabels, alertSeverityLabels, alertStatusLabels } from '@/types/compliance';

interface ExportComplianceProps {
  auditLogs?: AuditLogRecord[];
  alerts?: ComplianceAlert[];
  metrics?: ComplianceMetrics;
  period?: number;
}

export function ExportCompliance({ auditLogs, alerts, metrics, period = 30 }: ExportComplianceProps) {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const exportAuditToExcel = () => {
    if (!auditLogs || auditLogs.length === 0) {
      toast({
        title: 'Sin datos',
        description: 'No hay registros de auditoría para exportar',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);

    const columns: ExportColumn[] = [
      { key: 'sequence_number', header: 'Secuencia', width: 12 },
      { key: 'timestamp_utc', header: 'Fecha/Hora', width: 20, format: (v) => format(new Date(v), 'dd/MM/yyyy HH:mm:ss', { locale: es }) },
      { key: 'entity_type', header: 'Entidad', width: 20 },
      { key: 'action', header: 'Acción', width: 15 },
      { key: 'action_category', header: 'Categoría', width: 12, format: (v) => actionCategoryLabels[v as keyof typeof actionCategoryLabels] || v },
      { key: 'performed_by_name', header: 'Usuario', width: 25 },
      { key: 'performed_by_role', header: 'Rol', width: 15 },
      { key: 'sensitivity_level', header: 'Sensibilidad', width: 12, format: (v) => sensitivityLabels[v as keyof typeof sensitivityLabels] || v },
      { key: 'change_summary', header: 'Resumen', width: 40 },
      { key: 'record_hash', header: 'Hash', width: 20, format: (v) => v?.substring(0, 16) + '...' },
    ];

    try {
      exportToExcel({
        filename: 'auditoria_compliance',
        title: 'Registro de Auditoría',
        columns,
        data: auditLogs,
      });

      toast({
        title: 'Exportación exitosa',
        description: `${auditLogs.length} registros exportados a Excel`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo exportar el archivo',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportAuditToPDF = () => {
    if (!auditLogs || auditLogs.length === 0) {
      toast({
        title: 'Sin datos',
        description: 'No hay registros de auditoría para exportar',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);

    const columns: ExportColumn[] = [
      { key: 'sequence_number', header: '#', width: 8 },
      { key: 'timestamp_utc', header: 'Fecha', width: 25, format: (v) => format(new Date(v), 'dd/MM/yy HH:mm', { locale: es }) },
      { key: 'entity_type', header: 'Entidad', width: 20 },
      { key: 'action_category', header: 'Acción', width: 15, format: (v) => actionCategoryLabels[v as keyof typeof actionCategoryLabels] || v },
      { key: 'performed_by_name', header: 'Usuario', width: 25 },
      { key: 'sensitivity_level', header: 'Nivel', width: 12, format: (v) => sensitivityLabels[v as keyof typeof sensitivityLabels] || v },
    ];

    try {
      exportToPDF({
        filename: 'auditoria_compliance',
        title: 'Registro de Auditoría Inmutable',
        subtitle: `Athenea ERP - ISO 27001 / ENS / CMMI - Últimos ${period} días`,
        columns,
        data: auditLogs.slice(0, 100), // Limitar a 100 para PDF
      });

      toast({
        title: 'Exportación exitosa',
        description: 'Reporte PDF generado correctamente',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo generar el PDF',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportAlertsToPDF = () => {
    if (!alerts || alerts.length === 0) {
      toast({
        title: 'Sin datos',
        description: 'No hay alertas para exportar',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);

    const columns: ExportColumn[] = [
      { key: 'detected_at', header: 'Fecha', width: 25, format: (v) => format(new Date(v), 'dd/MM/yy HH:mm', { locale: es }) },
      { key: 'severity', header: 'Severidad', width: 15, format: (v) => alertSeverityLabels[v as keyof typeof alertSeverityLabels] || v },
      { key: 'title', header: 'Título', width: 35 },
      { key: 'status', header: 'Estado', width: 15, format: (v) => alertStatusLabels[v as keyof typeof alertStatusLabels] || v },
      { key: 'entity_type', header: 'Entidad', width: 20 },
    ];

    try {
      exportToPDF({
        filename: 'alertas_compliance',
        title: 'Alertas de Compliance',
        subtitle: `Athenea ERP - Centro de Alertas - Últimos ${period} días`,
        columns,
        data: alerts,
      });

      toast({
        title: 'Exportación exitosa',
        description: 'Reporte de alertas generado',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo generar el PDF',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportMetricsPDF = () => {
    if (!metrics) {
      toast({
        title: 'Sin datos',
        description: 'No hay métricas disponibles',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);

    // Crear datos de resumen para el PDF
    const summaryData = [
      { metric: 'Total Registros Auditoría', value: metrics.audit_metrics.total_records.toLocaleString() },
      { metric: 'Acciones Alto Riesgo', value: metrics.audit_metrics.high_risk_actions.toLocaleString() },
      { metric: 'Alertas Totales', value: metrics.alert_metrics.total_alerts.toLocaleString() },
      { metric: 'Alertas Abiertas', value: metrics.alert_metrics.open_alerts.toLocaleString() },
      { metric: 'Alertas Críticas', value: metrics.alert_metrics.critical_open.toLocaleString() },
      { metric: 'Violaciones SLA', value: metrics.alert_metrics.sla_breaches.toLocaleString() },
      { metric: 'Integridad Cadena', value: metrics.integrity_status.chain_valid ? 'VERIFICADA' : 'ERROR' },
      { metric: 'Registros Verificados', value: metrics.integrity_status.records_checked.toLocaleString() },
    ];

    const columns: ExportColumn[] = [
      { key: 'metric', header: 'Métrica', width: 50 },
      { key: 'value', header: 'Valor', width: 30 },
    ];

    try {
      exportToPDF({
        filename: 'resumen_compliance',
        title: 'Resumen Ejecutivo de Compliance',
        subtitle: `Athenea ERP - ISO 27001 / ENS / CMMI - Período: ${period} días`,
        columns,
        data: summaryData,
      });

      toast({
        title: 'Exportación exitosa',
        description: 'Resumen ejecutivo generado',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo generar el PDF',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isExporting}>
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Exportar Datos</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={exportMetricsPDF}>
          <FileText className="h-4 w-4 mr-2" />
          Resumen Ejecutivo (PDF)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportAuditToPDF}>
          <FileText className="h-4 w-4 mr-2" />
          Auditoría (PDF)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportAuditToExcel}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Auditoría (Excel)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={exportAlertsToPDF}>
          <FileText className="h-4 w-4 mr-2" />
          Alertas (PDF)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
